
import React, { useState, useMemo } from 'react';
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip } from 'recharts';
import { calculateMortgagePayment } from '../utils/mortgageUtils';
import type { MortgageData } from '../../CalculatorTabs';

type AmortizationTerm = '5' | '15' | '20' | '30';

interface AmortizationTabProps {
  mortgageData: MortgageData;
}

export const AmortizationTab: React.FC<AmortizationTabProps> = ({ mortgageData }) => {
  const [amortizationTerm, setAmortizationTerm] = useState<AmortizationTerm>('30');

  const amortizationData = useMemo(() => {
    const data = [];
    const { homePrice, downPayment, interestRate } = mortgageData;
    const loanAmount = homePrice - downPayment;
    const monthlyRate = interestRate / 100 / 12;
    const termYears = parseInt(amortizationTerm);
    const totalPayments = termYears * 12;
    const monthlyPayment = calculateMortgagePayment(loanAmount, interestRate, termYears);
    
    let remainingBalance = loanAmount;
    
    for (let year = 1; year <= termYears; year++) {
      let yearlyPrincipal = 0;
      let yearlyInterest = 0;
      
      for (let month = 1; month <= 12; month++) {
        if (remainingBalance <= 0) break;
        
        const interestPayment = remainingBalance * monthlyRate;
        const principalPayment = Math.min(monthlyPayment - interestPayment, remainingBalance);
        
        yearlyPrincipal += principalPayment;
        yearlyInterest += interestPayment;
        remainingBalance -= principalPayment;
      }
      
      data.push({
        year: `Year ${year}`,
        principal: Math.round(yearlyPrincipal),
        interest: Math.round(yearlyInterest)
      });
    }
    
    return data;
  }, [amortizationTerm, mortgageData]);

  return (
    <div className="bg-slate-900/60 border border-slate-700 rounded-lg p-6">
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-lg font-bold text-slate-100">
          Principal vs Interest Over Time
        </h3>
        
        <div className="flex items-center gap-3">
          <span className="text-sm text-slate-400">Term:</span>
          <ToggleGroup 
            type="single" 
            value={amortizationTerm} 
            onValueChange={(value: AmortizationTerm) => value && setAmortizationTerm(value)}
            className="justify-start"
          >
            <ToggleGroupItem 
              value="5" 
              className="data-[state=on]:bg-blue-600 data-[state=on]:text-white border-slate-600 text-slate-300"
            >
              5 Years
            </ToggleGroupItem>
            <ToggleGroupItem 
              value="15" 
              className="data-[state=on]:bg-blue-600 data-[state=on]:text-white border-slate-600 text-slate-300"
            >
              15 Years
            </ToggleGroupItem>
            <ToggleGroupItem 
              value="20" 
              className="data-[state=on]:bg-blue-600 data-[state=on]:text-white border-slate-600 text-slate-300"
            >
              20 Years
            </ToggleGroupItem>
            <ToggleGroupItem 
              value="30" 
              className="data-[state=on]:bg-blue-600 data-[state=on]:text-white border-slate-600 text-slate-300"
            >
              30 Years
            </ToggleGroupItem>
          </ToggleGroup>
        </div>
      </div>
      
      <div className="w-full h-96">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={amortizationData}>
            <XAxis 
              dataKey="year" 
              axisLine={false}
              tickLine={false}
              tick={{ fill: '#94a3b8', fontSize: 12 }}
              interval={Math.max(0, Math.floor(amortizationData.length / 8))}
            />
            <YAxis 
              axisLine={false}
              tickLine={false}
              tick={{ fill: '#94a3b8', fontSize: 12 }}
              tickFormatter={(value) => `$${(value / 1000)}k`}
            />
            <Tooltip
              contentStyle={{ 
                backgroundColor: '#0f172a', 
                border: '1px solid #334155',
                borderRadius: '8px',
                color: '#e2e8f0'
              }}
              formatter={(value, name) => [`$${Number(value).toLocaleString()}`, name === 'principal' ? 'Principal' : 'Interest']}
            />
            <Bar dataKey="interest" stackId="a" fill="#ef4444" />
            <Bar dataKey="principal" stackId="a" fill="#2563eb" />
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};
